import { Document, Page, Text, View, StyleSheet, Font, PDFDownloadLink, PDFViewer } from '@react-pdf/renderer'
import { Button, Card, Input, message } from 'antd'
import ActionsLayout from '@/components/MatchLayout'

// @ts-ignore
import Fangsong from '@/fonts/fangsong_GB2312.ttf'
import { useMatch } from '@/hooks/swr/useMatch'
import { ReactElement, useEffect, useState } from 'react'
import { GetServerSideProps } from 'next'
import { post } from '@/utils/request'
import useTokenGuard from '@/hooks/useTokenGuard'
import { getScoresMap } from '@/utils/utils'

Font.register({ family: 'Fangsong', src: Fangsong })

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Fangsong',
    padding: 30,
    fontSize: 12,
    lineHeight: 1.5,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 25, // 增加标题底部间距
  },
  section: {
    marginBottom: 20, // 增加章节间距
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8, // 增加标题与内容间距
  },
  sectionContent: {
    fontSize: 12,
    lineHeight: 1.5,
    marginBottom: 5, // 内容底部间距
  },
})

const MatchDocument = ({ match_id }) => {
  const { data: match, mutate: mutateMatch } = useMatch(match_id)
  // 付费守卫（点数不足引导充值/VIP + paid_feature 埋点）统一收敛到 useTokenGuard
  const { guard, tokenGuardModal, isVip } = useTokenGuard()

  const doGenerate = () => {
    post('matches/generate_match_document', { match_id }).then((res) => {
      if (res.ok) {
        message.success('生成成功')
        mutateMatch()
      } else {
        message.error(res.message)
      }
    })
  }

  const onGenerateClick = () => guard({ feature: 'match_document', cost: 1000, label: '生成赛事文档', onConfirm: doGenerate })

  const [turn, setTurn] = useState<any>(match.turn)
  const [deck, setDeck] = useState<any>(match.deck)
  const [time, setTime] = useState<any>(match.time)
  const [sortFactors, SetSortFactors] = useState<any>(match.sort_factors.flat())

  useEffect(() => {
    let factors = ''
    match.sort_factors.forEach((f) => {
      factors += `${getScoresMap(match.match_type)[f]},`
    })
    factors = factors.slice(0, -1)
    SetSortFactors(factors)
  }, [match.match_type, match.sort_factors])

  // PDF文档组件
  const PDFDocument = () => (
    <Document>
      <Page size="A4" style={styles.page}>
        <View>
          <Text style={styles.title}>掼蛋赛制文件</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>1、赛制</Text>
          <Text style={styles.sectionContent}>
            采用计时计副制，{turn} 轮积分编排。每轮比赛不超过 {time} 分钟（计时）、限打 {deck} 副牌（计副）。
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>2、升级及胜负</Text>
          <Text style={styles.sectionContent}>每一轮先过 A 或升级数高者胜，升级数相同则平。只有赢家才可升级，升级数为 3、2、1。</Text>
          <Text style={styles.sectionContent}>即:（1）双上（头游+二游），赢家升 3 级；</Text>
          <Text style={styles.sectionContent}>（2）头游+三游，赢家升 2 级；</Text>
          <Text style={styles.sectionContent}>（3）头游+末游，赢家升 1 级；</Text>
          <Text style={styles.sectionContent}>（4）打 A 必须赢家为双上或头游+三游，则过A。</Text>
          <Text style={styles.sectionContent}>3 轮总积分成绩按照{sortFactors}的顺序排列名次。</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>3、队伍组成</Text>
          <Text style={styles.sectionContent}>参赛最小单位为 2 人 1 队。队友固定，比赛开始后，原则上不允许更换队友。</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>4、编排</Text>
          <Text style={styles.sectionContent}>采取瑞士移位制，首轮随机编排，从第二轮开始，根据目前的总积分，高分对高分，依次编排； 已经对局过的对手不会再次相遇。</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>5、清点牌张</Text>
          <Text style={styles.sectionContent}>打牌前，双方点数扑克牌张数，多牌少牌即时调换。</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>6、洗牌、切牌、抓牌和领出</Text>
          <Text style={styles.sectionContent}>首副牌，由东家洗牌，南家切牌并翻出一张牌（如翻出的牌为王牌或红心 2，则重切重翻）。</Text>
          <Text style={styles.sectionContent}>从南家开始，根据翻出牌张的牌点，按逆时针方向点数出首抓者，抓到翻出牌张者为首圈领出牌者。</Text>
          <Text style={styles.sectionContent}>从第 2 副牌开始，由上游的上家洗牌，上游切牌，下游首抓。双下时，上游的下家首抓。</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>7、贡、还牌</Text>
          <Text style={styles.sectionContent}>贡牌为手中最大牌（不含红心级牌），还牌小于等于 10，双贡还牌牌面向下，还出后同时打开。</Text>
          <Text style={styles.sectionContent}>进贡点数相同时不得挑选，贡左还右。双贡方抓到两张大王则抗贡。</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>8、报牌</Text>
          <Text style={styles.sectionContent}>10 张内（含 10 张）主动报牌，只报一次。</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>9、记分</Text>
          <Text style={styles.sectionContent}>采用电子记分，每桌默认西家记分，北家复查。每轮成绩核查无误后提交。</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>10、成绩公布和查验</Text>
          <Text style={styles.sectionContent}>每轮比赛后，大屏幕公布当前轮次的比赛结果，供选手查验，比赛成绩公布五分钟后,不再受理修改更新。</Text>
          <Text style={styles.sectionContent}>如果比赛成绩有误，要在时效范围之内提出申诉。</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>11、赛场纪律</Text>
          <Text style={styles.sectionContent}>比赛全程，文明比赛，出牌规范；不得越序表态、出牌，不得传递非法信息等；赛场内禁止大声喧哗。</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>12、宣布每轮比赛结束</Text>
          <Text style={styles.sectionContent}>剩余 3 分钟时，不再开新牌，打完手中已抓或正在抓的牌，宣布比赛结束。</Text>
        </View>
      </Page>
    </Document>
  )

  return (
    <>
      <div className="mt-2 flex justify-around">
        <Card className="m-2 h-fit">
          <div className="flex flex-col gap-2">
            <Input className="w-40" prefix="比赛轮数:" value={turn} onChange={(e) => setTurn(e.target.value)} />
            <Input className="w-40" prefix="每轮副数:" value={deck} onChange={(e) => setDeck(e.target.value)} />
            <Input className="w-40" prefix="每轮时间:" value={time} onChange={(e) => setTime(e.target.value)} />
            <div>排名依据:</div>
            <Input.TextArea className="w-40" prefix="排名依据:" value={sortFactors} onChange={(e) => SetSortFactors(e.target.value)} />
            {match.show_document ? (
              <PDFDownloadLink className="m-2 h-fit w-fit" document={<PDFDocument />} fileName="赛制文档">
                <Button className="cursor-pointer" type="primary" size="large" onClick={() => message.success('导出成功')}>
                  导出赛制文件
                </Button>
              </PDFDownloadLink>
            ) : (
              <Button className="m-2 cursor-pointer" type="primary" size="large" onClick={onGenerateClick}>
                生成赛制文件{isVip ? '（VIP免费）' : ' (1000点)'}
              </Button>
            )}
          </div>
        </Card>
        {match.show_document ? (
          <PDFViewer
            style={{
              width: '210mm',
              height: '297mm',
            }}
          >
            <PDFDocument />
          </PDFViewer>
        ) : (
          <Card>
            <div className="pointer-events-none h-[500px] overflow-hidden">
              <PDFViewer
                showToolbar={false}
                style={{
                  width: '210mm',
                  height: '297mm',
                }}
              >
                <PDFDocument />
              </PDFViewer>
            </div>
            <div className="flex h-20 flex-col justify-end">
              <div className="text-xl font-bold text-yellow-600">生成文档后查看更多详细内容...</div>
            </div>
            <div
              className="absolute bottom-20 left-0 right-0 h-1/2 
                  bg-gradient-to-t from-white/90 via-white/50 to-transparent 
                  "
            />
          </Card>
        )}
      </div>

      {tokenGuardModal}
    </>
  )
}

MatchDocument.getLayout = function getLayout(children: ReactElement) {
  return <ActionsLayout>{children}</ActionsLayout>
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  return {
    props: {
      match_id: ctx.query.match_id,
    },
  }
}

export default MatchDocument
